import { Controller } from '@hotwired/stimulus'

declare const Stripe: any

const STRIPE_ELEMENT_STYLES = {
  base: {
    fontFamily: 'Lato, sans-serif',
    fontSize: '16px',
    color: '#424770',
    '::placeholder': { color: '#9ca7af' }
  },
  invalid: { color: '#fa755a' }
}

export default class extends Controller {
  static targets = [
    'addressElement',
    'addressErrors',
    'addressLine1',
    'addressLine2',
    'addressCity',
    'addressState',
    'addressPostalCode',
    'addressCountry'
  ]

  static values = {
    stripePublishableKey: String,
    mode: String,
    amount: Number
  }

  declare addressElementTarget: HTMLDivElement
  declare addressErrorsTarget: HTMLDivElement
  declare addressLine1Target: HTMLInputElement
  declare addressLine2Target: HTMLInputElement
  declare addressCityTarget: HTMLInputElement
  declare addressStateTarget: HTMLInputElement
  declare addressPostalCodeTarget: HTMLInputElement
  declare addressCountryTarget: HTMLInputElement
  declare hasAddressElementTarget: boolean
  declare hasAddressErrorsTarget: boolean
  declare hasAddressLine1Target: boolean
  declare hasAddressLine2Target: boolean
  declare hasAddressCityTarget: boolean
  declare hasAddressStateTarget: boolean
  declare hasAddressPostalCodeTarget: boolean
  declare hasAddressCountryTarget: boolean
  declare stripePublishableKeyValue: string
  declare modeValue: string
  declare amountValue: number

  private stripe: any
  private addressElement: any
  private addressState: any = {}
  private billingName: string = ''
  private addressComplete = false

  connect() {
    console.log('[AddressComponent] Controller connected', { mode: this.modeValue })
    this.initializeAddressElement()
  }

  disconnect() {
    console.log('[AddressComponent] Controller disconnected')
    if (this.addressElement) {
      this.addressElement.destroy()
    }
  }

  private initializeAddressElement() {
    if (!this.stripePublishableKeyValue) {
      console.warn('[AddressComponent] Stripe publishable key not available')
      return
    }

    if (!this.stripe) {
      this.stripe = Stripe(this.stripePublishableKeyValue)
    }

    const elementsConfig: any = {
      mode: this.modeValue || 'setup',
      currency: 'usd'
    }

    console.log('[AddressComponent] Initializing with:', {
      mode: this.modeValue,
      amountRaw: this.amountValue,
      amountType: typeof this.amountValue
    })

    // For payment mode, amount is required and must be > 0
    if (this.modeValue === 'payment') {
      const amountNumber = Number(this.amountValue)
      console.log('[AddressComponent] Converted amount:', amountNumber)
      
      if (!isNaN(amountNumber) && amountNumber > 0) {
        elementsConfig.amount = amountNumber
        console.log('[AddressComponent] Added amount to config:', amountNumber)
      } else {
        console.log('[AddressComponent] Amount is 0 or invalid - using setup mode instead of payment mode')
        elementsConfig.mode = 'setup'
      }
    }

    console.log('[AddressComponent] Final elements config:', elementsConfig)
    const elements = this.stripe.elements(elementsConfig)

    this.addressElement = elements.create('address', {
      mode: 'billing',
      fields: {
        phone: 'never'
      },
      defaultValues: {
        address: {
          country: this.addressCountryTarget?.value || 'US'
        }
      },
      style: STRIPE_ELEMENT_STYLES
    })

    const addressContainer = document.querySelector('#stripe_address')
    if (!addressContainer) {
      console.error('[AddressComponent] Address container element not found')
      return
    }

    try {
      this.addressElement.mount('#stripe_address')
    } catch (error) {
      console.error('[AddressComponent] Failed to mount Address Element:', error)
      return
    }

    this.addressElement.on('change', (event: any) => {
      // Extract both name and address data
      const addressData = event.value?.address || {}
      this.billingName = event.value?.name || ''
      
      this.addressState = addressData
      this.addressComplete = Boolean(event.complete)
      this.clearError()
      this.updateHiddenFields()
      this.dispatchAddressChange()
      console.log('[AddressComponent] Address ready:', this.addressComplete)
    })
  }

  getAddressState() {
    return this.addressState || {}
  }

  getBillingDetails() {
    // Return the full billing details in the format Stripe expects
    // Use values from both the stored address state AND the hidden form fields
    const line1 = this.hasAddressLine1Target ? this.addressLine1Target.value : this.addressState.line1
    const line2 = this.hasAddressLine2Target ? this.addressLine2Target.value : this.addressState.line2
    const city = this.hasAddressCityTarget ? this.addressCityTarget.value : this.addressState.city
    const state = this.hasAddressStateTarget ? this.addressStateTarget.value : this.addressState.state
    const postalCode = this.hasAddressPostalCodeTarget ? this.addressPostalCodeTarget.value : this.addressState.postal_code
    const country = this.hasAddressCountryTarget ? this.addressCountryTarget.value : this.addressState.country

    const address = {
      line1: line1 || this.addressState.line1,
      line2: line2 || this.addressState.line2,
      city: city || this.addressState.city,
      state: state || this.addressState.state,
      postal_code: postalCode || this.addressState.postal_code,
      country: country || this.addressState.country
    }

    console.log('[AddressComponent] getBillingDetails - stored state:', this.addressState)
    console.log('[AddressComponent] getBillingDetails - from fields:', { line1, city, state, postalCode, country })

    return {
      name: this.billingName,
      address: address
    }
  }

  private dispatchAddressChange(): void {
    // Dispatch event for other components to listen to
    this.element.dispatchEvent(new CustomEvent('address-changed', {
      detail: { address: this.addressState },
      bubbles: true
    }))
  }

  validate(): boolean {
    if (this.addressComplete) {
      this.clearError()
      return true
    }

    this.showError('Please complete your billing address.')
    return false
  }

  private showError(message: string): void {
    if (this.hasAddressErrorsTarget) {
      this.addressErrorsTarget.textContent = message
    }
  }

  private clearError(): void {
    if (this.hasAddressErrorsTarget) {
      this.addressErrorsTarget.textContent = ''
    }
  }

  private updateHiddenFields(): void {
    const fieldMap = {
      addressLine1: 'line1',
      addressLine2: 'line2',
      addressCity: 'city',
      addressState: 'state',
      addressPostalCode: 'postal_code',
      addressCountry: 'country'
    } as Record<string, string>

    Object.entries(fieldMap).forEach(([targetName, stateKey]) => {
      const target = this[`${targetName}Target` as keyof this] as HTMLInputElement | undefined
      if (target && target instanceof HTMLInputElement) {
        target.value = this.addressState[stateKey] || ''
      }
    })
  }
}
